"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  Home,
  Wrench,
  User,
  Code2,
  GitBranch,
  BarChart3,
  Layers,
  Users,
  Mail,
} from "lucide-react";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { Link } from "@/i18n/routing";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";
import { cn } from "@/lib/utils";
import { HamburgerButton } from "@/components/navigation/HamburgerButton";
import { NavDropdown } from "@/components/navigation/NavDropdown";

export function Navbar() {
  const t = useTranslations("Navigation");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const navItems = [
    { href: "/#home", label: t("home"), icon: Home },
    { href: "/#services", label: t("services"), icon: Wrench },
    { href: "/#about", label: t("about"), icon: User },
    { href: "/#skills", label: t("skills"), icon: Code2 },
    { href: "/#process", label: t("process"), icon: GitBranch },
    { href: "/#stats", label: t("stats"), icon: BarChart3 },
    { href: "/#portfolio", label: t("portfolio"), icon: Layers },
    { href: "/#partners", label: t("partners"), icon: Users },
    { href: "/#contact", label: t("contact"), icon: Mail },
  ];

  const handleToggle = useCallback(() => setMenuOpen((v) => !v), []);
  const handleClose = useCallback(() => setMenuOpen(false), []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-all duration-500 ease-in-out pointer-events-auto",
        scrolled || menuOpen
          ? "bg-(--bg-primary)/85 backdrop-blur-xl border-b border-(--border-subtle) h-20 shadow-sm"
          : "bg-transparent border-transparent h-24",
      )}
    >
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        <div className="shrink-0">
          <Link
            href="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <Image
              src="/images/main-logo.png"
              alt="DevDJ Logo"
              width={160}
              height={40}
              priority
              className="h-10 w-auto cursor-pointer object-contain"
            />
          </Link>
        </div>

        <div className="flex items-center gap-3 md:gap-0">
          <div className="hidden md:flex md:items-center md:gap-3 2xl:gap-8">
            <ul className="hidden 2xl:flex items-center gap-6">
              {navItems.map(({ href, label }) => (
                <li key={href}>
                  <a
                    href={href}
                    className="text-xs font-semibold uppercase tracking-[0.15em] text-(--text-primary) hover:text-(--brand-primary) transition-colors opacity-80 hover:opacity-100 cursor-pointer pointer-events-auto"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="hidden 2xl:block h-4 w-px bg-(--border-subtle)" />
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="2xl:hidden h-4 w-px bg-(--border-subtle)" />
          </div>

          <HamburgerButton
            isOpen={menuOpen}
            onToggle={handleToggle}
            ariaLabel={menuOpen ? t("close_menu") : t("open_menu")}
          />
        </div>
      </div>

      <NavDropdown isOpen={menuOpen} items={navItems} onClose={handleClose} />
    </header>
  );
}
